import { chapterColorOf, imageIdOf } from './autoImage.js';
import { plainRefs } from './refs.js';
import type { Connection, Display, Entity, FieldValue, NodeType, Story, StoryNode } from './types.js';

/** Indizierter, unveränderlicher Blick auf eine Story. Bei jeder Änderung neu erzeugen. */
export interface StoryCtx {
  readonly story: Story;
  readonly nodes: Map<string, StoryNode>;
  readonly types: Map<string, NodeType>;
  readonly entities: Map<string, Entity>;
  /** Kinder je Elternnode, nach Erstellungszeit */
  readonly children: Map<string, StoryNode[]>;
  /** Verbindungen je Node (beide Richtungen) */
  readonly links: Map<string, Connection[]>;
  readonly foundationals: Entity[];
}

export const cmpStr = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

export function createCtx(story: Story): StoryCtx {
  const nodes = new Map(story.nodes.map((n) => [n.id, n]));
  const types = new Map(story.types.map((t) => [t.id, t]));
  const entities = new Map(story.entities.map((e) => [e.id, e]));
  const children = new Map<string, StoryNode[]>();
  for (const n of story.nodes) {
    if (!n.parentId) continue;
    const list = children.get(n.parentId);
    if (list) list.push(n);
    else children.set(n.parentId, [n]);
  }
  for (const list of children.values()) list.sort((a, b) => cmpStr(a.createdAt, b.createdAt) || cmpStr(a.id, b.id));
  const links = new Map<string, Connection[]>();
  for (const c of story.connections) {
    for (const id of [c.sourceId, c.targetId]) {
      const list = links.get(id);
      if (list) list.push(c);
      else links.set(id, [c]);
    }
  }
  const foundationals = story.entities.filter((e) => types.get(e.typeId)?.category === 'foundational');
  return { story, nodes, types, entities, children, links, foundationals };
}

export function typeOf(ctx: StoryCtx, node: StoryNode): NodeType {
  const t = ctx.types.get(node.typeId);
  if (!t) throw new Error(`Typ ${node.typeId} existiert nicht`);
  return t;
}

/** Statische Felder: bei Instanzen die des Bibliothekseintrags (gelten für alle Kopien), sonst die eigenen. */
export function resolveFields(ctx: StoryCtx, node: StoryNode): Record<string, FieldValue> {
  if (node.entityId) return ctx.entities.get(node.entityId)?.fields ?? {};
  return node.fields;
}

const isFilled = (v: FieldValue | undefined): boolean => v !== null && v !== undefined && String(v).trim() !== '';

/** Automatisches Bild eines Nodes: bei Kopien das des Elements, bei Szenen in der Farbe ihres Kapitels (ohne Kapitel neutral) */
function imageOf(ctx: StoryCtx, node: StoryNode, type: NodeType, f: Record<string, FieldValue>): string | null {
  if (node.entityId) return imageIdOf(type, f, ctx.entities.get(node.entityId)?.colorSlot, node.entityId);
  if (type.category !== 'scene') return imageIdOf(type, f, node.colorSlot, node.id);
  const chapter = node.chapterId ? ctx.nodes.get(node.chapterId) : undefined;
  return chapter ? imageIdOf(type, f, chapter.colorSlot, chapter.id) : imageIdOf(type, f, null, node.id);
}

/** Anzeigedaten: Titel, Hover-Nachricht, Titelbild, Farben. Leere Nodes (kein Feld befüllt) werden als Kreis gezeigt. */
export function displayOf(ctx: StoryCtx, node: StoryNode): Display {
  const type = typeOf(ctx, node);
  const f = resolveFields(ctx, node);
  const title = String(f[type.titleKey] ?? '').trim();
  // Hover-Nachricht als reiner Text: „Lisa[@lisa_wege]“ erscheint als „Lisa“
  const hover = type.hoverKey ? plainRefs(String(f[type.hoverKey] ?? '').trim()) : '';
  const anyStatic = type.fields.some((d) => isFilled(f[d.key]));
  const anyDynamic = node.dynamicFields.some((d) => isFilled(d.value));
  return {
    title: title || type.name,
    hover,
    // eigenes Titelbild, sonst das Standardbild der Klasse, sonst das automatische
    imageId: imageOf(ctx, node, type, f),
    typeName: type.name,
    typeColor: type.color,
    tagColor: node.tagColor,
    chapterColor: type.category === 'scene' && !node.entityId ? chapterColorOf(ctx, node) : null,
    category: type.category,
    empty: !anyStatic && !anyDynamic && !node.documents.some((d) => d.text.trim() !== ''),
    refKey: node.entityId ? (ctx.entities.get(node.entityId)?.refKey ?? null) : null,
  };
}
